/**
 * Helpers for the SSO Apps Portal token handoff.
 *
 * The portal opens this app in a new tab with a `#session=<base64>` URL fragment containing the
 * full login-response blob (idtoken + per-app WGINB_<APP>_USER objects). A fresh tab on this
 * subdomain cannot read the portal's sessionStorage, so the fragment is the handoff channel.
 * Fragments are never sent to the server (no header weight, not in access logs).
 */

/** WGINB user-object key for THIS app in the portal session blob.
 *  = `WGINB_<APP_NAME>_USER`. Confirmed against a live portal `sessionStorage['wginb-session']` (2026-06-09). */
export const WGINB_USER_KEY = 'WGINB_INTERNS_USER';
export const WGINB_IDTOKEN_KEY = 'WGINB_IDTOKEN';

/** Decode the `#session=` fragment into the portal session object, or null if absent/malformed. */
export function decodeSessionFragment(): Record<string, any> | null {
    const hash = window.location.hash || '';
    const prefix = '#session=';
    if (!hash.startsWith(prefix)) {
        return null;
    }
    try {
        const encoded = hash.substring(prefix.length);
        // UTF-8-safe base64 decode (atob alone mangles non-Latin1 chars).
        const json = decodeURIComponent(escape(atob(encoded)));
        return JSON.parse(json);
    } catch {
        return null;
    }
}

/** The cookie handoff JSON.stringifies each value; the fragment stringifies the whole blob once.
 *  So inner values may be raw (object) or string — normalize defensively. */
export function unwrap(value: any): any {
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    }
    return value;
}

/** True if the JWT is missing or its exp claim has passed. */
export function isJwtExpired(idToken: string): boolean {
    try {
        const payload = JSON.parse(decodeURIComponent(escape(atob(idToken.split('.')[1]))));
        if (!payload.exp) {
            return true;
        }
        return payload.exp < Math.floor(Date.now() / 1000);
    } catch {
        return true;
    }
}

/** Remove the fragment from the address bar so the token isn't left in the URL/history. */
export function scrubFragment(): void {
    history.replaceState(null, '', window.location.pathname + window.location.search);
}
